/**
 * Network layer for Boost analytics events.
 *
 * Endpoint:
 *   - Defaults to `https://lambda.mybcapps.com/e` (prod).
 *   - Overridable via `BOOST_ANALYTICS_ENDPOINT` in `.env` (loaded by
 *     `dotenv-webpack` at build time). Boost provides no staging endpoint;
 *     set this to your own request-capture URL for local QA, then unset
 *     before merging to use the prod default.
 *
 * Delivery strategy:
 *   A single CORS `fetch` with `Content-Type: application/json` and
 *   `keepalive: true`. This mirrors Boost's own bundle, which POSTs JSON to
 *   the same `/e` endpoint over normal CORS (the endpoint returns CORS
 *   headers). `keepalive` lets the request survive the page unload that
 *   follows a click on a product tile.
 *
 *   We deliberately do NOT use `navigator.sendBeacon` or `mode: 'no-cors'`:
 *   no-cors silently downgrades the `application/json` Content-Type to
 *   `text/plain` (only safelisted values are allowed), which Boost's Lambda
 *   may then fail to parse. Boost's bundle uses neither.
 *
 * Errors are swallowed — analytics must never break the storefront. In
 * development, fires a `console.debug` so devs can see what's going out.
 */
const PROD_ENDPOINT = 'https://lambda.mybcapps.com/e';
function getEndpoint() {
    return process.env.BOOST_ANALYTICS_ENDPOINT || PROD_ENDPOINT;
}
const isDev = process.env.NODE_ENV === 'development';
// TODO: `boostSdDataNotSent` is a private, minified Boost key (verified on
// bc-widget-integration.js v1.83.0). If Boost renames it in a future release,
// deferral breaks SILENTLY — we keep writing to a dead key and the keepalive
// fallback never fires (it only triggers when Boost is *absent*, but Boost
// would still be present, just using a different key). Mitigate: feature-detect
// that Boost still drains the buffer (or pin/monitor the Boost bundle version)
// and degrade to the keepalive POST on mismatch.
/**
 * Boost's own "data not sent" buffer key. Writing our event here lets Boost's
 * widget bundle re-send it via its `checkSendEventWhenLoadPage` routine on the
 * NEXT page load (e.g. the PDP), so a click event survives the navigation
 * without relying on `keepalive`. Only used when `hasBoostSession()` is true;
 * otherwise we keepalive-POST.
 *
 * Delivery edges to know: Boost's flush is SINGLE-SLOT — last write wins, so a
 * burst of clicks before navigation keeps only the latest event — and it only
 * sends once `/cart.js` returns a cart token, so delivery can lag for a visitor
 * with no cart yet. It also requires Boost's bundle to load on the destination
 * page to run the flush.
 */
const BOOST_FLUSH_BUFFER_KEY = 'boostSdDataNotSent';
/**
 * Hand the event to Boost's buffer for deferred delivery on the next page.
 * Returns true if buffered, false on storage failure (caller then falls back
 * to an immediate keepalive POST).
 */
export function bufferForBoostFlush(payload) {
    try {
        localStorage.setItem(BOOST_FLUSH_BUFFER_KEY, JSON.stringify(payload));
        if (isDev) {
            // eslint-disable-next-line no-console
            console.debug('[boost-analytics] buffered for Boost PDP flush', payload.u || payload.a, payload);
        }
        return true;
    }
    catch (_a) {
        return false;
    }
}
export function postEvent(payload) {
    if (isDev) {
        // eslint-disable-next-line no-console
        console.debug('[boost-analytics]', payload.u || payload.a || 'event', payload);
    }
    // Normal CORS fetch with `application/json`, matching Boost's bundle.
    // `keepalive: true` keeps the request alive past page unload.
    try {
        fetch(getEndpoint(), {
            method: 'POST',
            body: JSON.stringify(payload),
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
        }).catch(() => {
            /* silent */
        });
    }
    catch (_a) {
        /* silent */
    }
}
